// Power (battle design, section 4): one honest number for how strong a creature is in a fight.
//
//   dps   = (2L/5 + 2)/50 × attack stat × kit power per second × traits and relic
//   kitPPS = basic/beat + Σ (move power × same-element − basic) / cooldown   (Light, Heavy, Signature; Speed discount)
//   bulk  = HP × (Defense + Resist) / 2
//   Power = 1.048 × ∛(dps × bulk)          an average-roll Lv 100 final is about 1,000
//
// Power (Hunt) is the creature as it hunts: real level, full rolls. Power (Arena) is the creature as it fights in the
// Arena, Halls and duels: fight level and PvP rolls (shared/pvp.ts). Coverage is left out: it fires only when it beats
// the basic attack, so on a neutral target it adds nothing.
import { SAME_ELEMENT, basicMove, beatMs, bonusOf, kitMoves, moveCooldownMs, type Fighter } from './combat.ts';
import { relicMult, type Relic } from './items.ts';
import { pvpFighter, type PvpMode } from './pvp.ts';
import { computeStats, creatureStats } from './stats.ts';
import type { Rolls } from './rolls.ts';
import { SPECIES } from './species.ts';
import { speciesTrait } from './traits.ts';

export const POWER_SCALE = 1.048;

export interface PowerCreature { species: string; level: number; rolls: Rolls; stars: number; relic: Relic | null; refine?: readonly number[]; train?: readonly number[] }

/** Power per second of a fighter's kit on a neutral target: the basic attack on every beat, each move's extra power over it per cooldown. */
export function kitPps(f: Fighter): number {
  const own = SPECIES[f.speciesId].elements as readonly string[];
  const same = (el: string) => (own.includes(el) ? SAME_ELEMENT : 1);
  const b = basicMove(f);
  const basic = b.power * same(b.element);
  let pps = basic / (beatMs(f) / 1000);
  for (const m of kitMoves(f)) {
    if (m.slot === 'coverage') continue;
    pps += (m.power * same(m.element) - basic) / (moveCooldownMs(f, m) / 1000);
  }
  return pps;
}

/** Damage per second and bulk of a fighter: the two halves of Power. */
export function powerParts(f: Fighter): { dps: number; bulk: number } {
  const sp = SPECIES[f.speciesId];
  const atk = sp.style === 'might' ? f.stats.might : f.stats.focus;
  const dps = (((2 * f.level) / 5 + 2) / 50) * atk * kitPps(f) * bonusOf(f);
  const bulk = (f.stats.hp * (f.stats.guard + f.stats.will)) / 2;
  return { dps, bulk };
}

/** Power of a fighter as it is (its level, stats, kit, traits). */
export function fighterPower(f: Fighter): number {
  const { dps, bulk } = powerParts(f);
  return Math.round(POWER_SCALE * Math.cbrt(Math.max(0, dps * bulk)));
}

function relicDmgOf(relic: Relic | null): Fighter['relicDmg'] {
  if (relic?.stat === 'element') return { element: relic.element, mult: relicMult(relic) };
  if (relic?.stat === 'damage') return { mult: relicMult(relic) };
  return undefined;
}

/** Power (Hunt): real level, full rolls, stars, refine and stone training, trait (as it acts on the species) and relic. No Hall Keeper bonus. */
export function huntPower(c: PowerCreature): number {
  return fighterPower({ speciesId: c.species, level: c.level, stats: creatureStats(c), trait: speciesTrait(c.rolls.trait, c.species), relicDmg: relicDmgOf(c.relic) });
}

/**
 * Power (Arena): as it fights in the Arena, Halls and duels (mode 'war': in Guild War), its trait as it acts on the
 * species (the live board rules, shared/board.ts ownTraits). Held items stay out, like type match-ups.
 */
export function arenaPower(c: PowerCreature, mode: PvpMode = 'arena'): number {
  const f = pvpFighter({ ...c, refine: c.refine ? [...c.refine] : undefined, held: null }, mode);
  return fighterPower({ ...f, trait: speciesTrait(f.trait, f.speciesId) });
}
